import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

interface OfflineQueueItem {
  id: string;
  type: 'flashcard_review' | 'quiz_answer' | 'study_progress';
  data: any;
  timestamp: number;
}

export const useOfflineMode = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState<OfflineQueueItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const { t } = useTranslation();
  const queueRef = useRef(offlineQueue);
  const isSyncingRef = useRef(isSyncing);

  useEffect(() => { queueRef.current = offlineQueue; }, [offlineQueue]);
  useEffect(() => { isSyncingRef.current = isSyncing; }, [isSyncing]);

  const syncOfflineData = useCallback(async () => {
    const queue = queueRef.current;
    if (queue.length === 0 || isSyncingRef.current) return;

    setIsSyncing(true);
    let syncedCount = 0;

    try {
      for (const item of queue) {
        try {
          logger.debug('Syncing item:', item.type);
          syncedCount++;
        } catch (err) {
          logger.error('Failed to sync item:', toError(err).message);
        }
      }

      setOfflineQueue([]);
      
      if (syncedCount > 0) {
        toast.success(t('common.offlineDataSynced', { count: syncedCount }));
      }
    } catch (err) {
      logger.error('Sync failed:', toError(err).message);
      toast.error(t('common.syncFailed'));
    } finally {
      setIsSyncing(false);
    }
  }, [t]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (queueRef.current.length > 0) {
        syncOfflineData();
      }
      toast.success(t('common.connectionRestored'));
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.info(t('common.offlineModeActive'));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncOfflineData, t]);

  useEffect(() => {
    const savedQueue = localStorage.getItem('offlineQueue');
    if (savedQueue) {
      try {
        setOfflineQueue(JSON.parse(savedQueue));
      } catch (err) {
        logger.error('Failed to parse offline queue:', toError(err).message);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('offlineQueue', JSON.stringify(offlineQueue));
  }, [offlineQueue]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'OFFLINE_SYNC') {
        syncOfflineData();
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      try { navigator.serviceWorker.removeEventListener('message', handler); } catch {}
    };
  }, [syncOfflineData]);

  const addToOfflineQueue = useCallback((item: Omit<OfflineQueueItem, 'id' | 'timestamp'>) => {
    const queueItem: OfflineQueueItem = {
      ...item,
      id: crypto.randomUUID(),
      timestamp: Date.now()
    };
    
    setOfflineQueue(prev => [...prev, queueItem]);
    toast.info(t('common.actionSavedOffline'));

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((reg) => {
          const anyReg = reg as any;
          if (anyReg.sync?.register) {
            return anyReg.sync.register('offline-sync');
          }
        })
        .catch(() => {});
    }
  }, [t]);

  return {
    isOnline,
    isOffline: !isOnline,
    offlineQueue,
    isSyncing,
    addToOfflineQueue,
    syncOfflineData,
    hasOfflineData: offlineQueue.length > 0
  };
};
